/**
 * Action Library Service
 *
 * Provides predefined action templates that map keywords to bone animations.
 * Actions can be matched from natural language text input.
 */

import { RigAction, ActionMatch } from '../types/RiggingTypes';

/**
 * Predefined action library with common character animations.
 * Each action includes keywords for matching and bone animations.
 */
export const ACTION_LIBRARY: RigAction[] = [
  {
    id: 'wave_hand',
    name: 'Wave Hand',
    keywords: [
      'wave',
      'waves',
      'waving',
      'hand',
      'hello',
      'hi',
      'bye',
      'goodbye',
      'greet',
      'greeting',
    ],
    description: 'Character waves their hand in a friendly greeting',
    duration: 1.5,
    boneAnimations: [
      {
        boneId: 'right_upper_arm',
        boneName: 'right_upper_arm',
        keyframes: [
          { time: 0, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.15, rotation: 60, translation: { x: 0, y: 0 } },
          { time: 0.3, rotation: 90, translation: { x: 0, y: 0 } },
          { time: 1, rotation: 90, translation: { x: 0, y: 0 } },
        ],
      },
      {
        boneId: 'right_forearm',
        boneName: 'right_forearm',
        keyframes: [
          { time: 0, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.3, rotation: 30, translation: { x: 0, y: 0 } },
          { time: 0.45, rotation: -30, translation: { x: 0, y: 0 } },
          { time: 0.6, rotation: 30, translation: { x: 0, y: 0 } },
          { time: 0.75, rotation: -30, translation: { x: 0, y: 0 } },
          { time: 0.9, rotation: 30, translation: { x: 0, y: 0 } },
          { time: 1, rotation: 0, translation: { x: 0, y: 0 } },
        ],
      },
    ],
  },
  {
    id: 'jump_joy',
    name: 'Jump with Joy',
    keywords: [
      'jump',
      'jumps',
      'jumping',
      'joy',
      'joyful',
      'happy',
      'excited',
      'celebrate',
      'celebration',
      'yay',
    ],
    description: 'Character jumps up with arms raised in celebration',
    duration: 1.2,
    boneAnimations: [
      {
        boneId: 'torso',
        boneName: 'torso',
        keyframes: [
          { time: 0, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.2, rotation: 0, translation: { x: 0, y: 10 } },
          { time: 0.4, rotation: 0, translation: { x: 0, y: -50 } },
          { time: 0.6, rotation: 0, translation: { x: 0, y: -50 } },
          { time: 0.8, rotation: 0, translation: { x: 0, y: 10 } },
          { time: 1, rotation: 0, translation: { x: 0, y: 0 } },
        ],
      },
      {
        boneId: 'left_upper_arm',
        boneName: 'left_upper_arm',
        keyframes: [
          { time: 0, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.3, rotation: -80, translation: { x: 0, y: 0 } },
          { time: 0.7, rotation: -80, translation: { x: 0, y: 0 } },
          { time: 1, rotation: 0, translation: { x: 0, y: 0 } },
        ],
      },
      {
        boneId: 'right_upper_arm',
        boneName: 'right_upper_arm',
        keyframes: [
          { time: 0, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.3, rotation: 80, translation: { x: 0, y: 0 } },
          { time: 0.7, rotation: 80, translation: { x: 0, y: 0 } },
          { time: 1, rotation: 0, translation: { x: 0, y: 0 } },
        ],
      },
    ],
  },
  {
    id: 'nod_head',
    name: 'Nod Head',
    keywords: [
      'nod',
      'nods',
      'nodding',
      'yes',
      'agree',
      'agreeing',
      'approval',
      'approve',
    ],
    description: 'Character nods their head in agreement',
    duration: 0.8,
    boneAnimations: [
      {
        boneId: 'head',
        boneName: 'head',
        keyframes: [
          { time: 0, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.25, rotation: 15, translation: { x: 0, y: 5 } },
          { time: 0.5, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.75, rotation: 15, translation: { x: 0, y: 5 } },
          { time: 1, rotation: 0, translation: { x: 0, y: 0 } },
        ],
      },
    ],
  },
  {
    id: 'shake_head',
    name: 'Shake Head',
    keywords: [
      'shake',
      'shakes',
      'shaking',
      'no',
      'disagree',
      'disagreeing',
      'denial',
      'refuse',
    ],
    description: 'Character shakes their head in disagreement',
    duration: 0.8,
    boneAnimations: [
      {
        boneId: 'head',
        boneName: 'head',
        keyframes: [
          { time: 0, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.2, rotation: -20, translation: { x: 0, y: 0 } },
          { time: 0.4, rotation: 20, translation: { x: 0, y: 0 } },
          { time: 0.6, rotation: -20, translation: { x: 0, y: 0 } },
          { time: 0.8, rotation: 20, translation: { x: 0, y: 0 } },
          { time: 1, rotation: 0, translation: { x: 0, y: 0 } },
        ],
      },
    ],
  },
  {
    id: 'walk',
    name: 'Walk',
    keywords: [
      'walk',
      'walks',
      'walking',
      'step',
      'steps',
      'stepping',
      'stroll',
      'move',
    ],
    description: 'Character performs a walking motion',
    duration: 1.0,
    boneAnimations: [
      {
        boneId: 'left_upper_leg',
        boneName: 'left_upper_leg',
        keyframes: [
          { time: 0, rotation: 30, translation: { x: 0, y: 0 } },
          { time: 0.5, rotation: -30, translation: { x: 0, y: 0 } },
          { time: 1, rotation: 30, translation: { x: 0, y: 0 } },
        ],
      },
      {
        boneId: 'right_upper_leg',
        boneName: 'right_upper_leg',
        keyframes: [
          { time: 0, rotation: -30, translation: { x: 0, y: 0 } },
          { time: 0.5, rotation: 30, translation: { x: 0, y: 0 } },
          { time: 1, rotation: -30, translation: { x: 0, y: 0 } },
        ],
      },
      {
        boneId: 'left_upper_arm',
        boneName: 'left_upper_arm',
        keyframes: [
          { time: 0, rotation: -20, translation: { x: 0, y: 0 } },
          { time: 0.5, rotation: 20, translation: { x: 0, y: 0 } },
          { time: 1, rotation: -20, translation: { x: 0, y: 0 } },
        ],
      },
      {
        boneId: 'right_upper_arm',
        boneName: 'right_upper_arm',
        keyframes: [
          { time: 0, rotation: 20, translation: { x: 0, y: 0 } },
          { time: 0.5, rotation: -20, translation: { x: 0, y: 0 } },
          { time: 1, rotation: 20, translation: { x: 0, y: 0 } },
        ],
      },
    ],
  },
  {
    id: 'clap',
    name: 'Clap Hands',
    keywords: ['clap', 'claps', 'clapping', 'applause', 'applaud', 'bravo'],
    description: 'Character claps their hands together',
    duration: 1.0,
    boneAnimations: [
      {
        boneId: 'left_upper_arm',
        boneName: 'left_upper_arm',
        keyframes: [
          { time: 0, rotation: 45, translation: { x: 0, y: 0 } },
          { time: 0.15, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.3, rotation: 45, translation: { x: 0, y: 0 } },
          { time: 0.45, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.6, rotation: 45, translation: { x: 0, y: 0 } },
          { time: 0.75, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 1, rotation: 45, translation: { x: 0, y: 0 } },
        ],
      },
      {
        boneId: 'right_upper_arm',
        boneName: 'right_upper_arm',
        keyframes: [
          { time: 0, rotation: -45, translation: { x: 0, y: 0 } },
          { time: 0.15, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.3, rotation: -45, translation: { x: 0, y: 0 } },
          { time: 0.45, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.6, rotation: -45, translation: { x: 0, y: 0 } },
          { time: 0.75, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 1, rotation: -45, translation: { x: 0, y: 0 } },
        ],
      },
    ],
  },
  {
    id: 'bow',
    name: 'Bow',
    keywords: [
      'bow',
      'bows',
      'bowing',
      'respect',
      'thank',
      'thanks',
      'grateful',
    ],
    description: 'Character bows respectfully',
    duration: 1.5,
    boneAnimations: [
      {
        boneId: 'torso',
        boneName: 'torso',
        keyframes: [
          { time: 0, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.3, rotation: 45, translation: { x: 0, y: 20 } },
          { time: 0.7, rotation: 45, translation: { x: 0, y: 20 } },
          { time: 1, rotation: 0, translation: { x: 0, y: 0 } },
        ],
      },
      {
        boneId: 'head',
        boneName: 'head',
        keyframes: [
          { time: 0, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.3, rotation: 30, translation: { x: 0, y: 0 } },
          { time: 0.7, rotation: 30, translation: { x: 0, y: 0 } },
          { time: 1, rotation: 0, translation: { x: 0, y: 0 } },
        ],
      },
    ],
  },
  {
    id: 'dance',
    name: 'Dance',
    keywords: ['dance', 'dances', 'dancing', 'groove', 'boogie', 'party'],
    description: 'Character performs a simple dance move',
    duration: 2.0,
    boneAnimations: [
      {
        boneId: 'torso',
        boneName: 'torso',
        keyframes: [
          { time: 0, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.25, rotation: -10, translation: { x: -10, y: -15 } },
          { time: 0.5, rotation: 10, translation: { x: 10, y: -15 } },
          { time: 0.75, rotation: -10, translation: { x: -10, y: -15 } },
          { time: 1, rotation: 0, translation: { x: 0, y: 0 } },
        ],
      },
      {
        boneId: 'left_upper_arm',
        boneName: 'left_upper_arm',
        keyframes: [
          { time: 0, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.25, rotation: -120, translation: { x: 0, y: 0 } },
          { time: 0.5, rotation: -60, translation: { x: 0, y: 0 } },
          { time: 0.75, rotation: -120, translation: { x: 0, y: 0 } },
          { time: 1, rotation: 0, translation: { x: 0, y: 0 } },
        ],
      },
      {
        boneId: 'right_upper_arm',
        boneName: 'right_upper_arm',
        keyframes: [
          { time: 0, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.25, rotation: 60, translation: { x: 0, y: 0 } },
          { time: 0.5, rotation: 120, translation: { x: 0, y: 0 } },
          { time: 0.75, rotation: 60, translation: { x: 0, y: 0 } },
          { time: 1, rotation: 0, translation: { x: 0, y: 0 } },
        ],
      },
    ],
  },
  {
    id: 'think',
    name: 'Think',
    keywords: [
      'think',
      'thinks',
      'thinking',
      'ponder',
      'pondering',
      'consider',
      'hmm',
      'wonder',
    ],
    description: 'Character poses in a thinking gesture',
    duration: 2.0,
    boneAnimations: [
      {
        boneId: 'right_upper_arm',
        boneName: 'right_upper_arm',
        keyframes: [
          { time: 0, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.3, rotation: 90, translation: { x: 0, y: 0 } },
          { time: 0.7, rotation: 90, translation: { x: 0, y: 0 } },
          { time: 1, rotation: 0, translation: { x: 0, y: 0 } },
        ],
      },
      {
        boneId: 'head',
        boneName: 'head',
        keyframes: [
          { time: 0, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.3, rotation: 10, translation: { x: 0, y: 0 } },
          { time: 0.7, rotation: 10, translation: { x: 0, y: 0 } },
          { time: 1, rotation: 0, translation: { x: 0, y: 0 } },
        ],
      },
    ],
  },
  {
    id: 'point',
    name: 'Point',
    keywords: [
      'point',
      'points',
      'pointing',
      'indicate',
      'show',
      'look',
      'there',
    ],
    description: 'Character points at something',
    duration: 1.0,
    boneAnimations: [
      {
        boneId: 'right_upper_arm',
        boneName: 'right_upper_arm',
        keyframes: [
          { time: 0, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.3, rotation: -70, translation: { x: 0, y: 0 } },
          { time: 0.7, rotation: -70, translation: { x: 0, y: 0 } },
          { time: 1, rotation: 0, translation: { x: 0, y: 0 } },
        ],
      },
      {
        boneId: 'right_forearm',
        boneName: 'right_forearm',
        keyframes: [
          { time: 0, rotation: 0, translation: { x: 0, y: 0 } },
          { time: 0.3, rotation: -20, translation: { x: 0, y: 0 } },
          { time: 0.7, rotation: -20, translation: { x: 0, y: 0 } },
          { time: 1, rotation: 0, translation: { x: 0, y: 0 } },
        ],
      },
    ],
  },
];

/**
 * Tokenize and normalize text for matching.
 */
function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .split(/\s+/)
    .filter((word) => word.length > 0);
}

/**
 * Match user text input to actions in the library.
 * Returns matches sorted by confidence (highest first).
 */
export function matchActions(text: string): ActionMatch[] {
  const tokens = tokenize(text);
  if (tokens.length === 0) return [];

  const matches: ActionMatch[] = [];

  for (const action of ACTION_LIBRARY) {
    const matchedKeywords: string[] = [];

    for (const token of tokens) {
      for (const keyword of action.keywords) {
        if (keyword.includes(token) || token.includes(keyword)) {
          if (!matchedKeywords.includes(keyword)) {
            matchedKeywords.push(keyword);
          }
        }
      }
    }

    if (matchedKeywords.length > 0) {
      const confidence =
        matchedKeywords.length /
        Math.max(tokens.length, action.keywords.length / 2);
      matches.push({
        action,
        confidence: Math.min(confidence, 1),
        matchedKeywords,
      });
    }
  }

  return matches.sort((a, b) => b.confidence - a.confidence);
}

/**
 * Get the best matching action for a text input.
 * Returns null if no good match is found.
 */
export function getBestAction(text: string): RigAction | null {
  const matches = matchActions(text);
  if (matches.length > 0 && matches[0].confidence > 0.3) {
    return matches[0].action;
  }
  return null;
}

/**
 * Get all available actions in the library.
 */
export function getAllActions(): RigAction[] {
  return ACTION_LIBRARY;
}

/**
 * Get an action by its ID.
 */
export function getActionById(id: string): RigAction | undefined {
  return ACTION_LIBRARY.find((action) => action.id === id);
}
